import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Blocks, ChevronDown, LayoutGrid, Loader2, Search } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIntegrations } from "../hooks/useApi";
import { APPS, categoriaDi, perCategoria } from "../components/integrations/registry";
import { useSalvaIntegrazioni } from "../components/integrations/shared";
import {
  AppTile,
  BrandCard,
  BrandStyles,
  HandArrow,
  MeshBackdrop,
  StatusPill,
  sfondoChiaro,
  statoApp,
  coloreApp,
  tintSurface,
  VARIABILI_SEZIONE,
} from "../components/integrations/brand";

// Da questo numero di app in su compare la ricerca.
const CERCA_DA = 8;
// Quanto scende la sfumatura dello sfondo sotto l'intestazione: sotto c'e'
// sempre almeno questo spazio, se no con poche schede verrebbe tagliata.
const CODA_SFONDO = 900;
// Lo sfondo dell'intestazione (brand.jsx → MeshBackdrop): "onboarding",
// "stelle", "antracite" o "perla", oppure "bianco" per nessuno sfondo. I testi sopra seguono: chiari
// sugli scuri, scuri sul chiaro e sul bianco.
const SFONDO = "onboarding";
const CON_SFONDO = SFONDO !== "bianco";
const TEMI = {
  scuro: {
    badge: "bg-white/10 ring-1 ring-white/15",
    badgeIcona: "text-violet-300",
    badgeTesto: "text-violet-200",
    titolo: "text-white",
    testo: "text-slate-300",
    cerca: "border-white/15 bg-white/10 text-white placeholder:text-slate-400 focus:border-white/40",
    cercaIcona: "text-slate-400",
    riepilogo: "text-slate-200",
    numero: "text-white",
    tenue: "text-slate-400",
    velo: "bg-[#0A0A0B]/55",
  },
  chiaro: {
    badge: "bg-[rgba(173,70,255,0.12)]",
    badgeIcona: "text-[#A855F7]",
    badgeTesto: "text-[#A855F7]",
    titolo: "text-[#020617]",
    testo: "text-[#475569]",
    cerca: "border-black/10 bg-white/80 text-[#020617] placeholder:text-[#94A3B8] focus:border-[#94A3B8]",
    cercaIcona: "text-[#94A3B8]",
    riepilogo: "text-[#334155]",
    numero: "text-[#020617]",
    tenue: "text-[#64748B]",
    velo: "bg-white/55",
  },
};
const TEMA = TEMI[!CON_SFONDO || sfondoChiaro(SFONDO) ? "chiaro" : "scuro"];

/**
 * Apps & Integrazioni: le app che il bot puo' usare in chat, in una griglia
 * di schede. Sopra le attive, sotto quelle da attivare, ciascuna sezione
 * richiudibile; le impostazioni di ciascuna stanno nella sua pagina
 * (IntegrationDetailPage). Le app vengono dal registro
 * (components/integrations/registry.js).
 *
 * Passando da un bot all'altro la rotta resta la stessa: la chiave rimonta la
 * pagina.
 */
export default function IntegrationsPage() {
  const { id: botId } = useParams();
  return <ElencoApp key={botId} botId={botId} />;
}

/**
 * Chiudendo una sezione la pagina si accorcia: se si e' scrollati in basso il
 * browser la farebbe risalire, trascinando il titolo appena premuto. Qui la
 * pagina tiene una "riserva" di altezza in fondo, solo quanto serve perche'
 * non scatti nulla; tornando su la riserva si consuma e sparisce.
 */
function useRiserva() {
  const [altezza, setAltezza] = useState(0);
  const attuale = useRef(0);

  const imposta = useCallback((valore) => {
    attuale.current = valore;
    setAltezza(valore);
  }, []);

  // Tiene solo la riserva che serve ancora (non la fa mai crescere).
  const ricalcola = useCallback(() => {
    if (!attuale.current) return;
    const senza = document.documentElement.scrollHeight - attuale.current;
    const serve = Math.max(0, Math.ceil(window.scrollY + window.innerHeight - senza));
    if (serve < attuale.current) imposta(serve);
  }, [imposta]);

  // Prima di chiudere: riserva quanto la sezione sta per togliere.
  const prenota = useCallback((px) => imposta(attuale.current + px), [imposta]);

  useLayoutEffect(() => {
    ricalcola();
  }, [altezza, ricalcola]);
  useEffect(() => {
    window.addEventListener("scroll", ricalcola, { passive: true });
    window.addEventListener("resize", ricalcola);
    return () => {
      window.removeEventListener("scroll", ricalcola);
      window.removeEventListener("resize", ricalcola);
    };
  }, [ricalcola]);

  return { altezza, prenota, ricalcola };
}

function ElencoApp({ botId }) {
  const navigate = useNavigate();
  const riserva = useRiserva();
  const { data, isLoading } = useIntegrations(botId);
  const { salva } = useSalvaIntegrazioni(botId);
  const [inCorso, setInCorso] = useState(null);
  const [cerca, setCerca] = useState("");

  // "Attiva" c'e' solo sulle app gia' pronte: le accende da qui. Le altre
  // hanno "Configura e attiva", che apre la loro pagina.
  const attiva = async (app) => {
    setInCorso(app.id);
    await salva({ [app.settingsKey]: { enabled: true } }, `${app.title}: attiva`);
    setInCorso(null);
  };

  const filtro = cerca.trim().toLowerCase();
  const visibili = APPS.filter(
    (app) => !filtro || `${app.title} ${app.description}`.toLowerCase().includes(filtro)
  );
  const attive = data ? visibili.filter((app) => data[app.settingsKey]?.enabled) : [];
  // Le app in arrivo hanno la loro sezione: "Da attivare" promette che si
  // possono accendere, e loro non si possono ancora.
  const daAttivare = data ? visibili.filter((app) => !app.inArrivo && !data[app.settingsKey]?.enabled) : [];
  const inArrivo = visibili.filter((app) => app.inArrivo);
  const tutteAttive = data ? APPS.filter((app) => data[app.settingsKey]?.enabled) : [];

  return (
    <div className="relative isolate" style={VARIABILI_SEZIONE}>
      <BrandStyles />

      {/* Se c'e', lo sfondo e' largo quanto la pagina: pieno dietro
          l'intestazione, poi sfuma piano nel bianco dietro le prime schede. */}
      <div className="relative">
        {CON_SFONDO ? <MeshBackdrop variante={SFONDO} coda={CODA_SFONDO} /> : null}
        {/* Con lo sfondo serve spazio sotto per la sfumatura (pb-28 = spazio 112). */}
        <header className={`pu-fade-up mx-auto max-w-[1400px] pt-6 sm:pt-10 ${CON_SFONDO ? "pb-28" : "pb-10"}`}>
          <span className={`inline-flex items-center gap-2 rounded-[20px] py-1 pl-2.5 pr-3.5 ${TEMA.badge}`}>
            <Blocks className={`h-4 w-4 ${TEMA.badgeIcona}`} strokeWidth={2} aria-hidden="true" />
            <span className={`text-sm font-semibold tracking-[0.07px] ${TEMA.badgeTesto}`}>Apps & Integrazioni</span>
          </span>
          {/* Sugli schermi larghi la ricerca sta a destra, sulla riga del
              titolo; su quelli stretti va sotto la descrizione. */}
          <div className="mt-4 grid gap-x-8 lg:grid-cols-[minmax(0,1fr)_auto]">
            <h1 className={`max-w-2xl text-[28px] font-semibold leading-9 tracking-tight sm:text-[32px] ${TEMA.titolo}`}>
              Cosa può fare il tuo assistente, oltre a rispondere
            </h1>
            {APPS.length >= CERCA_DA ? (
              <div className="relative order-last mt-5 w-full sm:max-w-sm lg:order-none lg:mt-0 lg:w-80 lg:self-center">
                <Search className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${TEMA.cercaIcona}`} />
                <input
                  value={cerca}
                  onChange={(e) => setCerca(e.target.value)}
                  placeholder="Cerca un'app"
                  aria-label="Cerca un'app"
                  className={`h-10 w-full rounded-lg border pl-9 pr-3 text-sm outline-none backdrop-blur ${TEMA.cerca}`}
                />
              </div>
            ) : null}
            <p className={`mt-3 max-w-xl text-base leading-7 ${TEMA.testo}`}>
              Attiva un'app e l'assistente la usa da solo, quando la conversazione lo chiede. Nella sua pagina
              decidi come e quando.
            </p>
          </div>
          {/* "N attive su M" conta solo le app che si possono attivare. */}
          {data ? <Riepilogo attive={tutteAttive} tutte={APPS.filter((app) => !app.inArrivo)} /> : null}
        </header>
      </div>

      <div className="pu-fade-up mx-auto max-w-[1400px] space-y-10 pb-10" style={CON_SFONDO ? { minHeight: CODA_SFONDO } : undefined}>

        {isLoading || !data ? (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full rounded-[14px]" />
            <Skeleton className="h-24 w-full rounded-[14px]" />
          </div>
        ) : (
          <>
            {/* Cercando, una sezione senza risultati sparisce: "Nessuna app
                attiva" sarebbe falso quando le attive sono solo nascoste. */}
            {attive.length || !filtro ? (
              <Gruppo titolo="Attive sull'assistente" conteggio={attive.length} riserva={riserva}>
                {attive.length ? (
                  <Griglia>
                    {attive.map((app) => (
                      <SchedaApp key={app.id} app={app} settings={data[app.settingsKey]} botId={botId} />
                    ))}
                  </Griglia>
                ) : (
                  <div className="rounded-[14px] border border-dashed border-[#CBD5E1] bg-white/90 px-5 py-5 backdrop-blur">
                    <p className="text-sm text-[#64748B]">
                      Nessuna app attiva: per ora l'assistente risponde solo a parole.
                    </p>
                  </div>
                )}
              </Gruppo>
            ) : null}

            {daAttivare.length ? (
              <GruppoCatalogo
                titolo="Da attivare"
                elenco={daAttivare}
                riserva={riserva}
                // Senza app attive, la nota accanto al titolo indica le app da cui partire.
                nota={
                  tutteAttive.length ? null : (
                    <>
                      parti da
                      <br />
                      una di queste
                    </>
                  )
                }
              >
                {(app) => (
                  <SchedaApp
                    key={app.id}
                    app={app}
                    settings={data[app.settingsKey]}
                    botId={botId}
                    onAttiva={() => attiva(app)}
                    inCorso={inCorso === app.id}
                    onConfigura={() => navigate(`/bots/${botId}/integrazioni/${app.id}`)}
                  />
                )}
              </GruppoCatalogo>
            ) : null}

            {inArrivo.length ? (
              <GruppoCatalogo
                titolo="In arrivo"
                elenco={inArrivo}
                riserva={riserva}
                etichettaFiltro="Filtra le app in arrivo per categoria"
              >
                {(app) => <SchedaApp key={app.id} app={app} settings={data[app.settingsKey]} botId={botId} />}
              </GruppoCatalogo>
            ) : null}

            {filtro && !visibili.length ? (
              <p className="text-sm text-[#64748B]">Nessuna app corrisponde a “{cerca.trim()}”.</p>
            ) : null}
          </>
        )}
      </div>
      <div aria-hidden="true" style={{ height: riserva.altezza }} />
    </div>
  );
}

// Quante carte mostrare nel mazzo prima di "+N".
const ICONE_RIEPILOGO = 10;
// Carte appoggiate a mano: ognuna un po’ storta, mai due uguali di fila.
const INCLINAZIONI = [-6, 4, -3, 5, -4, 3, -5, 4, -2, 6];

/**
 * Il riepilogo sotto il titolo: le app come un mazzo di carte sovrapposte,
 * prima le attive a colori e poi le altre sbiadite. Ogni carta sta sopra la
 * successiva; passandoci sopra il mazzo si apre a ventaglio.
 */
function Riepilogo({ attive, tutte }) {
  const ordinate = [...attive, ...tutte.filter((app) => !attive.includes(app))];
  const mostrate = ordinate.slice(0, ICONE_RIEPILOGO);
  const altre = ordinate.length - mostrate.length;
  // Carta dopo carta: la prima in cima, ognuna sotto la precedente.
  const carta = (i) =>
    `relative rounded-lg ring-2 ring-white shadow-[3px_3px_8px_rgba(15,23,42,0.22)] transition-[margin,transform] duration-300 ease-out rotate-[var(--inclinazione)] group-hover/mazzo:rotate-0 hover:!z-50 hover:-translate-y-1 ${
      i ? "-ml-1.5 group-hover/mazzo:ml-1.5" : ""
    }`;
  const inclinazione = (i) => ({ "--inclinazione": `${INCLINAZIONI[i % INCLINAZIONI.length]}deg` });

  return (
    <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2">
      <div className="group/mazzo flex items-center py-1">
        {mostrate.map((app, i) => {
          const accesa = attive.includes(app);
          return (
            <div
              key={app.id}
              title={accesa ? `${app.title}: attiva` : app.title}
              className={`group/carta ${carta(i)}`}
              style={{ zIndex: mostrate.length + 1 - i, ...inclinazione(i) }}
            >
              <div className={accesa ? "" : "grayscale transition-[filter] duration-300 group-hover/carta:grayscale-0"}>
                <AppTile app={app} size="sm" />
              </div>
              {/* Le spente sono carte piene ma velate: sotto non traspare niente. */}
              {accesa ? null : (
                <span className={`absolute inset-0 rounded-lg transition-opacity ${TEMA.velo} duration-300 group-hover/carta:opacity-0`} />
              )}
            </div>
          );
        })}
        {altre > 0 ? (
          <div
            className={`${carta(1)} flex h-8 min-w-8 items-center justify-center bg-white px-1.5 text-xs font-semibold text-[#475569]`}
            style={{ zIndex: 0, ...inclinazione(mostrate.length) }}
          >
            +{altre}
          </div>
        ) : null}
      </div>
      <p className={`text-sm ${TEMA.riepilogo}`}>
        {attive.length ? (
          <>
            <span className={`font-semibold ${TEMA.numero}`}>{attive.length}</span>{" "}
            {attive.length === 1 ? "attiva" : "attive"}
            <span className={TEMA.tenue}> su {tutte.length}</span>
          </>
        ) : (
          <>
            Nessuna attiva <span className={TEMA.tenue}>su {tutte.length}</span>
          </>
        )}
      </p>
    </div>
  );
}

/**
 * Una sezione del catalogo ("Da attivare", "In arrivo") con il suo filtro per
 * categoria. Se la categoria scelta resta vuota (ultima app attivata, o
 * nascosta dalla ricerca) si torna a "Tutte".
 */
function GruppoCatalogo({ titolo, elenco, riserva, etichettaFiltro, nota, children: scheda }) {
  const [categoria, setCategoria] = useState(null);
  const scelta = elenco.some((app) => categoriaDi(app.category).nome === categoria) ? categoria : null;
  const mostrate = scelta ? elenco.filter((app) => categoriaDi(app.category).nome === scelta) : elenco;
  return (
    <Gruppo
      titolo={titolo}
      riserva={riserva}
      conteggio={elenco.length}
      nota={nota}
      azioni={
        <FiltroCategoria
          gruppi={perCategoria(elenco)}
          totale={elenco.length}
          scelta={scelta}
          onScegli={setCategoria}
          etichetta={etichettaFiltro}
        />
      }
    >
      <Griglia key={scelta || "tutte"}>{mostrate.map(scheda)}</Griglia>
    </Gruppo>
  );
}

/**
 * La nota a mano a sinistra del titolo di una sezione, fuori dalla colonna,
 * con la freccia che curva verso la prima scheda. Fuori dal flusso: non sposta
 * niente. C'e' solo sugli schermi larghi, dove a sinistra c'e' spazio; sugli
 * altri resta nascosta (misurata, ma invisibile).
 */
function NotaTitolo({ children }) {
  const nota = useRef(null);
  const [visibile, setVisibile] = useState(false);

  useLayoutEffect(() => {
    const titolo = nota.current?.parentElement;
    const pagina = titolo?.closest("main");
    if (!titolo || !pagina) return undefined;
    // Lo spazio fra il bordo della pagina e il titolo deve contenere la nota.
    const misura = () => {
      const spazio = titolo.getBoundingClientRect().left - pagina.getBoundingClientRect().left;
      setVisibile(spazio >= (nota.current?.offsetWidth || 0) + 20);
    };
    misura();
    if (typeof ResizeObserver === "undefined") return undefined;
    // Anche aprendo e chiudendo la barra laterale, non solo col ridimensiona.
    const osserva = new ResizeObserver(misura);
    osserva.observe(pagina);
    return () => osserva.disconnect();
  }, []);

  return (
    <div
      ref={nota}
      aria-hidden="true"
      className={`pointer-events-none absolute right-full top-0 z-10 mr-4 hidden text-right sm:block ${
        visibile ? "" : "invisible"
      }`}
    >
      <p
        className="whitespace-nowrap text-sm leading-snug tracking-[0.7px] text-[#64748B]"
        style={{ fontFamily: "'Fuzzy Bubbles', cursive" }}
      >
        {children}
      </p>
      <HandArrow className="ml-auto mr-6 mt-1 -rotate-[55deg]" />
    </div>
  );
}

/** Una sezione dell'elenco, che si chiude e si riapre dal titolo. */
function Gruppo({ titolo, conteggio, azioni, riserva, nota, children }) {
  const [aperto, setAperto] = useState(true);
  // Chiudendo, prima le schede escono (l'entrata al contrario), poi la
  // sezione si nasconde.
  const [uscendo, setUscendo] = useState(false);
  // Ogni riapertura rimonta le schede, cosi' rientrano con la loro animazione.
  const [aperture, setAperture] = useState(0);
  const contenuto = useRef(null);
  const attesa = useRef(null);
  const id = useId();
  useEffect(() => () => clearTimeout(attesa.current), []);

  // Nasconde la sezione tenendo fermo il titolo: la pagina non si accorcia
  // sotto lo scroll (useRiserva). 16px = lo spazio tra titolo e schede.
  const nascondi = () => {
    riserva?.prenota((contenuto.current?.offsetHeight || 0) + 16);
    setUscendo(false);
    setAperto(false);
  };

  const chiudi = () => {
    const schede = contenuto.current?.querySelectorAll(".pu-carta-in").length || 0;
    const fermo = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (!schede || fermo) return nascondi();
    setUscendo(true);
    attesa.current = setTimeout(nascondi, durataUscita(schede));
  };

  // Riaprendo la pagina si riallunga: la riserva non serve piu'.
  const ricalcola = riserva?.ricalcola;
  useLayoutEffect(() => {
    if (aperto) ricalcola?.();
  }, [aperto, ricalcola]);

  const cambia = () => {
    if (uscendo) {
      // Ripensamento a meta' uscita: le schede tornano (e rientrano).
      clearTimeout(attesa.current);
      setUscendo(false);
      return;
    }
    if (aperto) return chiudi();
    setAperture((n) => n + 1);
    setAperto(true);
  };
  const visibile = aperto && !uscendo;

  return (
    <section className="space-y-4">
      {/* Il titolo sta in un'etichetta bianca: sopra la sfumatura si legge a
          qualsiasi altezza, scura o chiara. A destra, se ci sono, i filtri. */}
      {/* Riga ad altezza fissa: aprendo e chiudendo non si sposta nulla. */}
      <div className="flex min-h-9 flex-wrap items-center justify-between gap-3">
      <div className="relative flex items-center gap-3">
      {nota && visibile ? <NotaTitolo>{nota}</NotaTitolo> : null}
      <h2 className="text-sm font-semibold text-[#020617]">
        <button
          type="button"
          onClick={cambia}
          aria-expanded={visibile}
          aria-controls={id}
          className="group/titolo inline-flex items-center gap-2 rounded-full bg-white/90 py-1.5 pl-2.5 pr-3.5 shadow-[0_2px_8px_rgba(15,23,42,0.12)] outline-none ring-1 ring-black/5 backdrop-blur focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronDown
            className={`h-4 w-4 text-[#94A3B8] transition-transform group-hover/titolo:text-[#020617] ${
              visibile ? "" : "-rotate-90"
            }`}
            aria-hidden="true"
          />
          {titolo}
          <span className="font-normal text-[#64748B]">{conteggio}</span>
        </button>
      </h2>
      </div>
      {azioni ? (
        // Da chiusa il menu resta al suo posto, solo invisibile: la riga non
        // cambia altezza e il titolo non si muove.
        <div
          aria-hidden={!visibile}
          className={`transition-[opacity,visibility] duration-150 ${visibile ? "" : "invisible opacity-0"}`}
        >
          {azioni}
        </div>
      ) : null}
      </div>
      {/* Aprendo le schede entrano una dopo l'altra (Griglia); chiudendo escono
          al contrario, dall'ultima alla prima, e poi la sezione si nasconde. */}
      <div id={id} ref={contenuto} hidden={!aperto} className={uscendo ? "pu-uscita pointer-events-none" : undefined}>
        <div key={aperture}>{children}</div>
      </div>
    </section>
  );
}

// Valore della voce "Tutte le categorie" (il menu non accetta un valore vuoto).
const TUTTE = "__tutte";

/** Una voce del menu delle categorie: icona, nome e quante app. */
function VoceCategoria({ icon: Icon, nome, quante }) {
  return (
    <span className="flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 shrink-0 text-[#64748B]" aria-hidden="true" />
      {nome}
      <span className="text-[#94A3B8]">{quante}</span>
    </span>
  );
}

/** Il filtro per categoria: un menu con "Tutte le categorie" e una voce per categoria. */
function FiltroCategoria({ gruppi, totale, scelta, onScegli, etichetta = "Filtra per categoria" }) {
  if (gruppi.length < 2) return null;
  return (
    <Select value={scelta || TUTTE} onValueChange={(valore) => onScegli(valore === TUTTE ? null : valore)}>
      <SelectTrigger
        aria-label={etichetta}
        className="h-9 w-60 rounded-full border-black/10 bg-white/90 pl-3.5 pr-3 text-xs font-medium text-[#334155] shadow-[0_2px_8px_rgba(15,23,42,0.08)] backdrop-blur focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-ring"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent position="popper" sideOffset={6} className="rounded-xl border-black/10 shadow-[0_10px_30px_rgba(15,23,42,0.12)]">
        <SelectItem value={TUTTE} className="rounded-lg text-xs">
          <VoceCategoria icon={LayoutGrid} nome="Tutte le categorie" quante={totale} />
        </SelectItem>
        {gruppi.map((gruppo) => (
          <SelectItem key={gruppo.nome} value={gruppo.nome} className="rounded-lg text-xs">
            <VoceCategoria icon={gruppo.icon} nome={gruppo.nome} quante={gruppo.apps.length} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// La griglia mette quante schede ne entrano (5 per riga alla larghezza
// massima della pagina).
// Le schede entrano una dopo l'altra: 12ms di distanza, al massimo 12 passi
// (poi entrano insieme, per non far aspettare con tante app).
// Uscendo lo stesso, al contrario: l'ultima scheda esce per prima.
const PASSO_ENTRATA = 12;
const MAX_PASSI = 12;
// Deve restare uguale alla durata di .pu-carta-in in brand.jsx.
const DURATA_CARTA = 180;

/** Quanto dura l'uscita di `schede` schede (l'ultima parte dopo tutte le altre). */
function durataUscita(schede) {
  return DURATA_CARTA + Math.min(schede - 1, MAX_PASSI) * PASSO_ENTRATA;
}

function Griglia({ children }) {
  const quante = React.Children.count(children);
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4">
      {React.Children.map(children, (scheda, i) => (
        <div
          className="pu-carta-in grid"
          style={{
            "--ritardo": `${Math.min(i, MAX_PASSI) * PASSO_ENTRATA}ms`,
            "--ritardo-uscita": `${Math.min(quante - 1 - i, MAX_PASSI) * PASSO_ENTRATA}ms`,
          }}
        >
          {scheda}
        </div>
      ))}
    </div>
  );
}

/**
 * La scheda di un'app nella griglia. Tutta la scheda apre la sua pagina (il
 * titolo e' un link esteso); l'azione in basso resta cliccabile per conto suo.
 */
function SchedaApp({ app, settings = {}, botId, onAttiva, inCorso, onConfigura }) {
  const stato = statoApp(app, settings);
  const pagina = `/bots/${botId}/integrazioni/${app.id}`;

  return (
    // Una sola superficie: il colore dell'app in alto a destra che sfuma nel bianco.
    <BrandCard
      className="group relative flex flex-col p-4 transition-[border-color,box-shadow] hover:border-[#CBD5E1] hover:shadow-[0_5px_5px_rgba(0,0,0,0.06)]"
      style={tintSurface(coloreApp(app))}
    >
      <div className="flex items-start justify-between gap-2">
        <AppTile app={app} />
        <StatusPill state={stato} suColore />
      </div>
      {app.inArrivo ? (
        // In arrivo: la scheda non si apre, come nella Panoramica.
        <p className="mt-4 text-[15px] font-semibold text-[#020617]">{app.title}</p>
      ) : (
        <Link
          to={pagina}
          className="mt-4 text-[15px] font-semibold text-[#020617] outline-none after:absolute after:inset-0 after:rounded-[14px] focus-visible:after:ring-2 focus-visible:after:ring-ring"
        >
          {app.title}
        </Link>
      )}
      <p className="mt-1 line-clamp-2 flex-1 text-xs leading-[18px] text-[#475569]">{app.description}</p>

      {/* Attiva: da dove prende il "quando" e Configura. Da attivare: solo il
          pulsante, largo quanto la scheda. */}
      <div className="mt-4 flex items-center justify-between gap-2">
        {settings.enabled ? (
          <span className="min-w-0 truncate text-[11px] leading-4 text-[#94A3B8]">
            {settings.use_when ? "Quando: su misura" : "Quando: suggerito"}
          </span>
        ) : null}
        <AzioneApp
          settings={settings}
          stato={stato}
          pagina={pagina}
          onAttiva={onAttiva}
          inCorso={inCorso}
          onConfigura={onConfigura}
        />
      </div>
    </BrandCard>
  );
}

/** L'azione della scheda: Configura (attiva), Attiva (pronta) o Configura e attiva. */
function AzioneApp({ settings, stato, pagina, onAttiva, inCorso, onConfigura }) {
  if (stato === "inArrivo") {
    // Stesso pulsante delle altre schede, spento: la scheda resta uguale.
    return (
      <button
        type="button"
        disabled
        title="In arrivo"
        className="inline-flex h-9 w-full cursor-not-allowed items-center justify-center gap-1 rounded-[10px] bg-[#F8FAFC] px-3 text-xs font-semibold text-[#94A3B8]"
      >
        Configura
      </button>
    );
  }
  if (settings.enabled) {
    return (
      <Link
        to={pagina}
        className="relative z-10 ml-auto inline-flex shrink-0 items-center gap-1 rounded-lg px-1.5 py-1 text-xs font-semibold text-[#020617]"
      >
        Configura
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
      </Link>
    );
  }
  if (stato === "spenta") {
    return (
      <button
        type="button"
        onClick={onAttiva}
        disabled={inCorso}
        className="relative z-10 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-[10px] bg-[#0F172A] px-3 text-xs font-semibold text-[#F8FAFC] transition-colors hover:bg-[#1E293B] disabled:opacity-60"
      >
        {inCorso ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
        Attiva
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onConfigura}
      className="relative z-10 inline-flex h-9 w-full items-center justify-center gap-1 rounded-[10px] bg-[#F1F5F9] px-3 text-xs font-semibold text-[#18181B] transition-colors hover:bg-[#E2E8F0]"
    >
      Configura e attiva
      <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}
